/* SteelPulse · M Studio — steelpulse.js
   Vanilla JS for filter/sort, variant selection, cart AJAX, TOC scroll-spy
   All Shopify AJAX Cart API calls use the standard /cart/* endpoints.
   ================================================================ */

(function () {
  'use strict';

  /* ── UTILS ─────────────────────────────────────────────────── */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  /* ── HEADER SCROLL ─────────────────────────────────────────── */
  (function initHeader() {
    const header = $('.sp-header');
    if (!header) return;
    let last = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.style.transform = y > last && y > 120 ? 'translateY(-100%)' : '';
      header.style.transition = 'transform 280ms cubic-bezier(0.22,1,0.36,1)';
      last = y;
    }, { passive: true });
  })();

  /* ── COLLECTION FILTER & SORT ──────────────────────────────── */
  (function initCollection() {
    const grid = $('#CollectionGrid');
    if (!grid) return;

    const pills     = $$('.filter-pill');
    const sortTabs  = $$('.sort-tab');
    const viewBtns  = $$('[data-view-btn]');
    const countEl   = $('#ProductCount');
    const cards     = $$('.product-card', grid);

    let activeFilter = 'All';
    let activeSort   = 'Newest';

    function getPrice(card) {
      return parseFloat(card.dataset.price || 0);
    }

    function apply() {
      let visible = cards.slice();

      // filter
      if (activeFilter !== 'All') {
        visible = visible.filter(c => c.dataset.set === activeFilter);
      }

      // sort
      if (activeSort === 'Price ↑') visible.sort((a, b) => getPrice(a) - getPrice(b));
      if (activeSort === 'Price ↓') visible.sort((a, b) => getPrice(b) - getPrice(a));

      // hide all, show visible
      cards.forEach(c => c.closest('[data-card-wrap]')?.classList.add('hidden'));
      visible.forEach(c => c.closest('[data-card-wrap]')?.classList.remove('hidden'));

      // re-order visible in DOM
      visible.forEach(c => grid.appendChild(c.closest('[data-card-wrap]') || c));

      if (countEl) countEl.textContent = visible.length + ' pieces';
    }

    pills.forEach(btn => {
      btn.addEventListener('click', () => {
        pills.forEach(b => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        activeFilter = btn.dataset.filter;
        apply();
      });
    });

    sortTabs.forEach(btn => {
      btn.addEventListener('click', () => {
        sortTabs.forEach(b => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        activeSort = btn.dataset.sort;
        apply();
      });
    });

    viewBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const v = btn.dataset.viewBtn;
        grid.dataset.view = v;
        viewBtns.forEach(b => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
      });
    });
  })();

  /* ── PRODUCT PAGE ──────────────────────────────────────────── */
  (function initProduct() {
    const form = $('#ProductForm');
    if (!form) return;

    // Gallery thumbnails
    const thumbs = $$('.product-gallery__thumb');
    const mainImg = $('#GalleryMain');
    thumbs.forEach((th, i) => {
      th.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        th.classList.add('active');
        if (mainImg) mainImg.src = th.dataset.src;
      });
    });

    // Variant pills
    const variantInput = $('#VariantId');
    $$('.variant-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        const group = pill.dataset.group;
        $$('.variant-pill[data-group="' + group + '"]').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        updateVariant();
      });
    });

    function updateVariant() {
      // Build selected options object
      const selected = {};
      $$('.variant-pill.active').forEach(p => { selected[p.dataset.group] = p.dataset.value; });
      // Find matching variant from window.productVariants set by theme
      const variants = window.productVariants || [];
      const match = variants.find(v =>
        v.options.every((opt, i) => opt === selected[i + 1])
      );
      if (match) {
        if (variantInput) variantInput.value = match.id;
        const priceEl = $('#ProductPrice');
        if (priceEl && match.price) {
          priceEl.textContent = formatMoney(match.price);
        }
      }
    }

    // Add to cart
    const atcBtn = $('#AddToCart');
    if (atcBtn) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        atcBtn.textContent = 'Adding…';
        atcBtn.disabled = true;
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variantInput ? variantInput.value : form.querySelector('[name="id"]')?.value,
              quantity: 1,
            }),
          });
          if (!res.ok) throw new Error('Add to cart failed');
          atcBtn.textContent = 'Added —';
          updateCartCount();
          setTimeout(() => { atcBtn.textContent = 'Add to bag →'; atcBtn.disabled = false; }, 2000);
        } catch (err) {
          atcBtn.textContent = 'Try again';
          atcBtn.disabled = false;
        }
      });
    }
  })();

  /* ── CART PAGE ─────────────────────────────────────────────── */
  (function initCart() {
    const cartForm = $('#CartForm');
    if (!cartForm) return;

    $$('[data-qty-btn]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const line = btn.closest('[data-line]');
        const input = line?.querySelector('.qty-input input');
        if (!input) return;
        const delta = btn.dataset.qtyBtn === 'plus' ? 1 : -1;
        const newQty = Math.max(0, parseInt(input.value, 10) + delta);
        input.value = newQty;
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line.dataset.line, 10), quantity: newQty }),
        });
        if (!window.Shopify?.designMode) location.reload();
      });
    });
  })();

  /* ── CART COUNT ────────────────────────────────────────────── */
  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const data = await res.json();
      const bubbles = $$('.cart-bubble-count');
      bubbles.forEach(b => {
        b.textContent = data.item_count;
        b.style.display = data.item_count > 0 ? 'block' : 'none';
      });
    } catch (_) {}
  }
  updateCartCount();

  /* ── POLICIES TOC SCROLL-SPY ───────────────────────────────── */
  (function initTOC() {
    const toc = $$('.toc-link');
    if (!toc.length) return;
    const sections = toc.map(l => document.getElementById(l.dataset.target)).filter(Boolean);

    function onScroll() {
      let current = sections[0];
      sections.forEach(s => {
        if (s.getBoundingClientRect().top < 200) current = s;
      });
      toc.forEach(l => l.setAttribute('aria-current', l.dataset.target === current?.id ? 'true' : 'false'));
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    toc.forEach(l => {
      l.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.getElementById(l.dataset.target);
        if (target) window.scrollTo({ top: target.offsetTop - 96, behavior: 'smooth' });
      });
    });
  })();

  /* ── CONTACT FORM TOPIC PILLS ──────────────────────────────── */
  (function initContact() {
    const topicInput = $('#ContactTopic');
    $$('.topic-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        $$('.topic-pill').forEach(b => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        if (topicInput) topicInput.value = btn.dataset.topic;
      });
    });
  })();

  /* ── HELPER ────────────────────────────────────────────────── */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

})();
